import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowDown, ChevronDown, Clock, TriangleAlert, type LucideIcon } from 'lucide-react';
import { NoGpsBadge } from '@/components/common/NoGpsBadge';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { formatCount } from '@/utils/number';
import { scrollIntoContainer } from '@/utils/scrollIntoContainer';
import {
  DAY_LIST_SEPARATOR,
  DCR_APPROVAL_STATUS_LABELS,
  DCR_APPROVAL_STATUS_TONE,
  HIGHLIGHT_RING_CLASS,
} from '../constants/moTravel.constants';
import type { MoTravelDay, MoTravelStop } from '../types/moTravel.types';
import { getStartTitle, getStopLabel, sortLatestDayFirst, type DayRoute } from '../utils/dayRoutes';
import { formatDuration } from '../utils/formatDuration';
import { formatEntryCount } from '../utils/formatEntryCount';
import { formatDayRouteKm } from '../utils/formatKm';
import { buildStopInsights, countDayAlerts, type StopInsight } from '../utils/stopInsights';
import { getStartKey, getStopKey, isKeyOnDay } from '../utils/stopKey';
import { MoEndIcon } from './MoEndIcon';
import { MoStartIcon } from './MoStartIcon';
import { MoTransportBadge } from './MoTransportBadge';

const UNNAMED_LOCATION_LABEL = 'Unnamed location';

const ACTIVE_DAY_HEADER_CLASS = cn(
  'shadow-sm ring-1 ring-inset',
  'bg-[color:color-mix(in_srgb,var(--day-color)_10%,var(--card))]',
  'ring-[color:color-mix(in_srgb,var(--day-color)_40%,transparent)]',
);

type DayHeaderStyle = CSSProperties & Record<'--day-color', string>;

const ROW_STATE_CLASS = {
  selected: 'bg-highlight/20 hover:bg-highlight/20',
  hovered: 'bg-highlight/10 hover:bg-highlight/10',
} as const;

const getStopRowSelector = (waypointKey: string): string => `[data-stop-key="${waypointKey}"]`;

const getDaySectionSelector = (date: string): string => `[data-day-date="${date}"]`;

const findDateOfKey = (routes: DayRoute[], waypointKey: string | null): string | null =>
  routes.find((route) => isKeyOnDay(waypointKey, route.day.date))?.day.date ?? null;

function getFirstEntryKey(day: MoTravelDay): string | null {
  if (day.start) return getStartKey(day);
  const [firstStop] = day.stops;
  return firstStop ? getStopKey(day, firstStop) : null;
}

function getRowStateClass(isSelected: boolean, isHovered: boolean): string | false {
  if (isSelected) return ROW_STATE_CLASS.selected;
  if (isHovered) return ROW_STATE_CLASS.hovered;
  return false;
}

interface StopInteractionProps {
  hoveredKey: string | null;
  onStopHover: (waypointKey: string | null) => void;
  onStopSelect: (waypointKey: string) => void;
}

interface WarningPillProps {
  icon?: LucideIcon;
  children: ReactNode;
}

function WarningPill({ icon: Icon, children }: WarningPillProps) {
  return (
    <span
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1 rounded-full px-1.5 py-px text-2xs font-semibold',
        STATUS_COLOR_CLASS.warning,
      )}
    >
      {Icon && <Icon className="size-3" aria-hidden />}
      {children}
    </span>
  );
}

interface TimelineDotProps {
  label: ReactNode;
  color: string | null;
  isHighlighted: boolean;
}

function TimelineDot({ label, color, isHighlighted }: TimelineDotProps) {
  return (
    <span
      className={cn(
        'relative z-10 flex size-6 shrink-0 items-center justify-center rounded-full text-2xs font-bold ring-2 ring-card motion-safe:transition-transform',
        color ? 'text-white' : 'bg-muted text-muted-foreground',
        isHighlighted && cn('scale-110', HIGHLIGHT_RING_CLASS),
      )}
      style={color ? { background: color } : undefined}
      aria-hidden
    >
      {label}
    </span>
  );
}

interface LegRowProps {
  isLongGap: boolean;
}

function LegRow({ isLongGap }: LegRowProps) {
  return (
    <li className="-ml-5 flex items-center gap-1.5 py-0.5 pl-3.5 text-muted-foreground">
      <span className="relative z-10 flex size-3 items-center justify-center rounded-full bg-card">
        <ArrowDown className="size-3" aria-hidden />
      </span>
      {isLongGap && <WarningPill>Long gap</WarningPill>}
    </li>
  );
}

interface TimelineRowProps extends StopInteractionProps {
  rowKey: string;
  isSelected: boolean;
  children: ReactNode;
}

function TimelineRow({
  rowKey,
  isSelected,
  hoveredKey,
  onStopHover,
  onStopSelect,
  children,
}: TimelineRowProps) {
  return (
    <li data-stop-key={rowKey} className="-ml-5 scroll-mt-20">
      <button
        type="button"
        onClick={() => onStopSelect(rowKey)}
        onMouseEnter={() => onStopHover(rowKey)}
        onMouseLeave={() => onStopHover(null)}
        onFocus={() => onStopHover(rowKey)}
        onBlur={() => onStopHover(null)}
        aria-current={isSelected ? 'step' : undefined}
        className={cn(
          'flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/60',
          'focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none',
          getRowStateClass(isSelected, rowKey === hoveredKey),
        )}
      >
        {children}
      </button>
    </li>
  );
}

interface StopRowProps extends StopInteractionProps {
  rowKey: string;
  stop: MoTravelStop;
  color: string | null;
  isEnd: boolean;
  insight: StopInsight | undefined;
  isSelected: boolean;
}

function StopRow({
  rowKey,
  stop,
  color,
  isEnd,
  insight,
  isSelected,
  ...interaction
}: StopRowProps) {
  const isHovered = rowKey === interaction.hoveredKey;

  return (
    <>
      {insight && <LegRow isLongGap={insight.isLongGap} />}
      <TimelineRow rowKey={rowKey} isSelected={isSelected} {...interaction}>
        <TimelineDot
          label={isEnd ? <MoEndIcon className="size-3.5" /> : getStopLabel(stop)}
          color={color}
          isHighlighted={isSelected || isHovered}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">
              {stop.name ?? stop.instituteName ?? UNNAMED_LOCATION_LABEL}
            </span>
            {!stop.hasLocation && <NoGpsBadge />}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatTime(stop.timeFrom)}
            {stop.timeTo ? ` – ${formatTime(stop.timeTo)}` : ''}
            {' · '}
            {formatDuration(stop.spendMinutes)}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {stop.dcrTypeCode && <Badge variant="outline">{stop.dcrTypeCode}</Badge>}
            <StatusBadge tone={DCR_APPROVAL_STATUS_TONE[stop.approvalStatus]}>
              {DCR_APPROVAL_STATUS_LABELS[stop.approvalStatus]}
            </StatusBadge>
          </span>
          {stop.instituteName && stop.name && (
            <span className="truncate text-xs text-muted-foreground">{stop.instituteName}</span>
          )}
          {insight?.lateMinutes != null && (
            <WarningPill icon={Clock}>
              Submitted {formatDuration(insight.lateMinutes)} later
            </WarningPill>
          )}
        </span>
        {stop.transportType && (
          <MoTransportBadge transportType={stop.transportType} tintColor={null} size="small" />
        )}
      </TimelineRow>
    </>
  );
}

interface DaySectionProps extends StopInteractionProps {
  route: DayRoute;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  focusedKey: string | null;
}

function DaySection({ route, isOpen, onOpenChange, focusedKey, ...interaction }: DaySectionProps) {
  const { day, color } = route;
  const hasFocusedStop = isKeyOnDay(focusedKey, day.date);

  const waypointKindByKey = useMemo(
    () => new Map(route.waypoints.map((waypoint) => [waypoint.key, waypoint.kind])),
    [route.waypoints],
  );
  const insights = useMemo(() => buildStopInsights(day), [day]);
  const alertCount = useMemo(() => countDayAlerts(day, insights), [day, insights]);

  const getDotColor = (rowKey: string): string | null =>
    waypointKindByKey.has(rowKey) ? color : null;

  const dayHeaderStyle: DayHeaderStyle = { '--day-color': color };
  const dayStartKey = getStartKey(day);
  const isStartSelected = dayStartKey === focusedKey;
  const isStartHovered = dayStartKey === interaction.hoveredKey;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={onOpenChange}
      data-day-date={day.date}
      className="scroll-mt-35 lg:scroll-mt-2"
    >
      <CollapsibleTrigger
        style={dayHeaderStyle}
        className={cn(
          'sticky top-35 z-20 flex w-full items-center justify-between gap-2 rounded-lg bg-muted px-3 py-2 text-left lg:top-2',
          'before:pointer-events-none before:absolute before:inset-x-0 before:-top-2 before:h-2 before:bg-card',
          'focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none',
          (isOpen || hasFocusedStop) && ACTIVE_DAY_HEADER_CLASS,
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span
            className="h-8 w-1 shrink-0 rounded-full"
            style={{ background: color }}
            aria-hidden
          />
          <span className="flex min-w-0 flex-col">
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold">{formatDisplayDate(day.date)}</span>
              {alertCount > 0 && (
                <WarningPill icon={TriangleAlert}>{formatCount(alertCount, 'alert')}</WarningPill>
              )}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {formatEntryCount(day.totalEntry)} · {formatDayRouteKm(day.routeKm)}
              {day.startTime ? ` · in at ${formatTime(day.startTime)}` : ''}
            </span>
          </span>
        </span>
        <ChevronDown
          className={cn('size-4 shrink-0 motion-safe:transition-transform', isOpen && 'rotate-180')}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="relative mt-1 ml-5 flex flex-col gap-0.5 border-l border-dashed border-border pl-0">
          {day.start && (
            <TimelineRow rowKey={dayStartKey} isSelected={isStartSelected} {...interaction}>
              <TimelineDot
                label={<MoStartIcon className="size-3.5" />}
                color={color}
                isHighlighted={isStartSelected || isStartHovered}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-sm font-medium">Start · {getStartTitle(day)}</span>
                <span className="text-xs text-muted-foreground">
                  {day.startTime
                    ? `Logged in ${formatTime(day.startTime)}`
                    : 'Login time not recorded'}
                </span>
              </span>
            </TimelineRow>
          )}
          {day.stops.map((stop) => {
            const rowKey = getStopKey(day, stop);
            return (
              <StopRow
                key={rowKey}
                rowKey={rowKey}
                stop={stop}
                color={getDotColor(rowKey)}
                isEnd={waypointKindByKey.get(rowKey) === 'end'}
                insight={insights.get(rowKey)}
                isSelected={rowKey === focusedKey}
                {...interaction}
              />
            );
          })}
        </ol>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface MoActivityTimelineProps extends StopInteractionProps {
  routes: DayRoute[];
  focusedKey: string | null;
  emptyMessage: string;
}

export function MoActivityTimeline({
  routes,
  focusedKey,
  emptyMessage,
  ...interaction
}: MoActivityTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const latestFirstRoutes = useMemo(() => sortLatestDayFirst(routes), [routes]);
  const dateListKey = latestFirstRoutes.map((route) => route.day.date).join(DAY_LIST_SEPARATOR);
  const getDefaultExpandedDate = (): string | null => findDateOfKey(latestFirstRoutes, focusedKey);
  const [expandedDate, setExpandedDate] = useState(getDefaultExpandedDate);
  const [expandedDateListKey, setExpandedDateListKey] = useState(dateListKey);
  const [lastFocusedKey, setLastFocusedKey] = useState(focusedKey);
  const [dayScrollRequestCount, setDayScrollRequestCount] = useState(0);
  const firstEntryDateByKey = useMemo(
    () =>
      new Map(
        latestFirstRoutes.flatMap((route) => {
          const firstEntryKey = getFirstEntryKey(route.day);
          return firstEntryKey ? [[firstEntryKey, route.day.date] as const] : [];
        }),
      ),
    [latestFirstRoutes],
  );

  if (dateListKey !== expandedDateListKey) {
    setExpandedDateListKey(dateListKey);
    setExpandedDate(getDefaultExpandedDate());
  }
  if (focusedKey !== lastFocusedKey) {
    setLastFocusedKey(focusedKey);
    const focusedDate = findDateOfKey(latestFirstRoutes, focusedKey);
    if (focusedDate) setExpandedDate(focusedDate);
  }

  useEffect(() => {
    if (!focusedKey) return;
    const firstEntryDate = firstEntryDateByKey.get(focusedKey);
    const scrollTargetSelector = firstEntryDate
      ? getDaySectionSelector(firstEntryDate)
      : getStopRowSelector(focusedKey);
    const frameId = requestAnimationFrame(() => {
      const scrollTarget = containerRef.current?.querySelector<HTMLElement>(scrollTargetSelector);
      if (scrollTarget) scrollIntoContainer(scrollTarget, 'start');
    });
    return () => cancelAnimationFrame(frameId);
  }, [focusedKey, firstEntryDateByKey, dayScrollRequestCount]);

  function handleDayOpenChange(route: DayRoute, isOpen: boolean) {
    if (!isOpen) {
      setExpandedDate(null);
      return;
    }
    setExpandedDate(route.day.date);
    const firstEntryKey = getFirstEntryKey(route.day);
    if (!firstEntryKey) return;
    interaction.onStopSelect(firstEntryKey);
    setDayScrollRequestCount((requestCount) => requestCount + 1);
  }

  if (routes.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {latestFirstRoutes.map((route) => (
        <DaySection
          key={route.day.date}
          route={route}
          isOpen={route.day.date === expandedDate}
          onOpenChange={(isOpen) => handleDayOpenChange(route, isOpen)}
          focusedKey={focusedKey}
          {...interaction}
        />
      ))}
    </div>
  );
}
